import { type LoaderFunctionArgs } from '@remix-run/node';
import { useMatchesHandleData } from '~/modules/root';
import type { SerializeFrom } from '@remix-run/node';
import { Outlet, useLoaderData } from '@remix-run/react';
import { BuildHubDetailsHeader } from '~/modules/build-hub/components/header/BuildHubDetailsHeader';
import { ManageCollabratorModal } from '~/modules/build-hub/components/build-hub-admin/ManageCollabratorModal';
import { EditBuildHubModal } from '~/modules/build-hub/components/build-hub-admin/EditBuildHubModal';
import { SelectBuildHubBoardModal } from '~/modules/build-hub/components/build-hub-design-boards/SelectBuildHubBoardModal';

import {
    mergeMeta,
    createMetaDescription,
    createOGTags,
    getCanonicalHreflang,
} from '~/modules/seo';

import { acceptBuildHubInviteLoader } from '@modules/build-hub/loaders/accept-build-hub-invite-loader.server';
import { buildDetailsHubLoader } from '@modules/build-hub/loaders/build-hub-details-loader.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { BuildHubRealTimeProvider } from '@modules/build-hub/context/BuildHubRealTimeContext';
import React from 'react';

export const BUILD_HUB_ROOT_ROUTE = 'routes/_app._pm.app.project.$id';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;
        const title = `${data?.buildHub?.title} | ArchiPro`;
        const description = createMetaDescription([]);

        return [
            {
                title,
            },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
        ];
    }
);

type LoaderHandlerType = 'accept-invite' | 'build-hub';

const getLoaderHandlerType = (args: LoaderFunctionArgs): LoaderHandlerType => {
    const url = new URL(args.request.url);
    const intent = url.searchParams.get('intent');
    const invite = url.searchParams.get('invite');

    if (intent === 'BuildHubInvite' && invite) return 'accept-invite';
    return 'build-hub';
};

export const loader = async (args: LoaderFunctionArgs) => {
    switch (getLoaderHandlerType(args)) {
        case 'accept-invite':
            return acceptBuildHubInviteLoader(args); // throws redirect
        case 'build-hub':
        default:
            return buildDetailsHubLoader(args); // normal flow
    }
};

export type BuildHubRootData = SerializeFrom<typeof loader>;

const BuildHubDetailsRoot = () => {
    const { buildHub } = useLoaderData<typeof loader>();

    // Get the current route's handle

    const hideBuildHubDefaultHeader = useMatchesHandleData<boolean>(
        'hideBuildHubDefaultHeader',
        false
    );
    const { setCategory } = useCurrentCategory();
    React.useEffect(() => {
        setCategory('Projects');
        return () => setCategory(null);
    }, [setCategory]);

    return (
        <BuildHubRealTimeProvider buildHubId={Number(buildHub.id)}>
            <div>
                {!hideBuildHubDefaultHeader && (
                    <BuildHubDetailsHeader buildHub={buildHub} />
                )}
                <Outlet />
                <ManageCollabratorModal />
                <EditBuildHubModal />
                <SelectBuildHubBoardModal />
            </div>
        </BuildHubRealTimeProvider>
    );
};

export const handle = {
    hideTopNavigation: {
        desktop: true,
    },
    hideFooter: {
        desktop: true,
    },
};

export default BuildHubDetailsRoot;
